import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PencilIcon } from '@heroicons/react/outline';
import IconButton from '../Buttons/IconButton';
import PrimaryButton from '../Buttons/PrimaryButton';
import PriceInputPresentational from '../Form/PriceInputPresentational';
import { PaymentResponse } from '../../interfaces/payments';
import { formatCurrency } from '../../libs/utils/helpers';

interface EditPaymentAmountModalProps {
  payment?: PaymentResponse;
  participantName: string;
  entityLabel: string;
  onSubmit: (data: { id?: number; amount: number }) => void;
  disabled?: boolean;
  isLoading: boolean;
}

const EditPaymentAmountModal: React.FC<EditPaymentAmountModalProps> = ({
  payment,
  participantName,
  entityLabel,
  onSubmit,
  disabled = false,
  isLoading,
}) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const submittedRef = useRef(false);

  const currentAmount = payment?.data?.attributes?.amount;
  const hasReceipt = !!payment?.data?.attributes?.receipt?.data?.id;

  useEffect(() => {
    if (!open) return;
    setAmount(currentAmount || currentAmount === 0 ? String(currentAmount) : '');
    setError('');
  }, [currentAmount, open]);

  useEffect(() => {
    if (isLoading) {
      submittedRef.current = true;
    }

    if (submittedRef.current && !isLoading) {
      submittedRef.current = false;
      setOpen(false);
    }
  }, [isLoading]);

  if (!payment) return null;

  const onSave = () => {
    const parsedAmount = Number(String(amount).replace(',', '.'));

    if (amount === '' || !Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setError("L'importo deve essere un numero maggiore o uguale a 0");
      return;
    }

    onSubmit({
      id: payment.data.id,
      amount: parsedAmount,
    });
  };

  return (
    <>
      <IconButton
        orangeTxt
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <PencilIcon className='w-5 h-5' />
      </IconButton>

      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as='div'
          className='fixed z-10 inset-0 overflow-y-auto'
          onClose={setOpen}
        >
          <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <Dialog.Overlay className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
            </Transition.Child>

            <span
              className='hidden sm:inline-block sm:align-middle sm:h-screen'
              aria-hidden='true'
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <div className='relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full sm:p-6'>
                <h4 className='text-1.5xl font-IBM font-light text-gray-700 mb-6'>
                  Modifica quota partecipazione
                </h4>

                <div className='space-y-4 text-sm text-gray-700'>
                  <p>
                    {participantName} - {entityLabel}
                  </p>
                  <p>Importo attuale: {formatCurrency(currentAmount)}</p>

                  {hasReceipt && (
                    <div className='px-3 py-2 text-sm text-orange-800 rounded-lg bg-orange-50 border border-orange-200'>
                      Questo pagamento ha gia una ricevuta associata. La modifica
                      aggiornera solo l'importo del pagamento.
                    </div>
                  )}

                  <PriceInputPresentational
                    id='paymentAmount'
                    label='Nuovo importo'
                    name='paymentAmount'
                    type='number'
                    min='0'
                    step='0.01'
                    value={amount}
                    onChange={(event) => {
                      setAmount(event.target.value);
                      setError('');
                    }}
                    error={
                      error
                        ? ({ message: error, type: 'validate' } as any)
                        : undefined
                    }
                  />
                </div>

                <div className='flex items-center justify-end gap-4 mt-6'>
                  <PrimaryButton
                    textSmall
                    outline
                    onClick={() => setOpen(false)}
                    disabled={isLoading}
                  >
                    Annulla
                  </PrimaryButton>
                  <PrimaryButton
                    small
                    onClick={onSave}
                    isLoading={isLoading}
                    disabled={isLoading}
                  >
                    Salva
                  </PrimaryButton>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default EditPaymentAmountModal;
